import { useState, useCallback, useRef, useEffect } from 'react';
import { useNodesState, useEdgesState } from '@xyflow/react';
import type { Node, Edge, Connection } from '@xyflow/react';
import { playbookApi } from '../api/playbookApi';
import type {
  PlaybookEntity,
  PlaybookStatusFull,
  PlaybookTransitionFull,
  PlaybookStatusCategory,
} from '../types/playbook.types';

export interface StatusNodeData {
  label: string;
  slug: string;
  category: PlaybookStatusCategory;
  description?: string;
  is_initial?: boolean;
  is_terminal?: boolean;
  sla_hours?: number | null;
  escalation_role?: string | null;
  statusId: string;
  selected?: boolean;
  [key: string]: unknown;
}

export interface TransitionEdgeData {
  label: string;
  slug: string;
  transitionId: string;
  trigger_mode: string;
  guardCount: number;
  validatorCount: number;
  actionCount: number;
  is_collaborative: boolean;
  selected?: boolean;
  [key: string]: unknown;
}

function statusToNode(s: PlaybookStatusFull): Node<StatusNodeData> {
  return {
    id: s.id,
    type: 'statusNode',
    position: { x: s.position_x ?? 0, y: s.position_y ?? 0 },
    data: {
      label: s.name,
      slug: s.slug,
      category: s.category,
      description: s.description,
      is_initial: s.is_initial,
      is_terminal: s.is_terminal,
      sla_hours: s.sla_hours,
      escalation_role: s.escalation_role,
      statusId: s.id,
    },
  };
}

function transitionToEdge(t: PlaybookTransitionFull): Edge<TransitionEdgeData> | null {
  if (!t.from_status_id || !t.to_status_id) return null;
  return {
    id: t.id,
    source: t.from_status_id,
    target: t.to_status_id,
    type: 'transitionEdge',
    data: {
      label: t.name,
      slug: t.slug,
      transitionId: t.id,
      trigger_mode: t.trigger_mode,
      guardCount: t.guards?.length ?? 0,
      validatorCount: t.validators?.length ?? 0,
      actionCount: t.actions?.length ?? 0,
      is_collaborative: t.is_collaborative,
    },
  };
}

let slugCounter = 0;
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '') || `status_${++slugCounter}`;
}

export function usePlaybookEditor(playbookId: string) {
  const [playbook, setPlaybook] = useState<PlaybookEntity | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<StatusNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<TransitionEdgeData>>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [drawingEdge, setDrawingEdge] = useState(false);
  const playbookRef = useRef(playbook);
  playbookRef.current = playbook;

  const loadPlaybook = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await playbookApi.getPlaybook(playbookId);
      const pb = (resp as any)?.playbook ?? (resp as any)?.data?.playbook ?? resp as any;
      setPlaybook(pb);

      const savedLayout = pb.diagram_layout as Record<string, { x: number; y: number }> | undefined;

      const nodeList = (pb.statuses ?? []).map((s: PlaybookStatusFull) => {
        const pos = savedLayout?.[s.id];
        const n = statusToNode(s);
        if (pos) n.position = { x: pos.x, y: pos.y };
        return n;
      });

      const edgeList = (pb.transitions ?? [])
        .map(transitionToEdge)
        .filter(Boolean) as Edge<TransitionEdgeData>[];

      setNodes(nodeList);
      setEdges(edgeList);
      setDirty(false);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load playbook');
    } finally {
      setLoading(false);
    }
  }, [playbookId, setNodes, setEdges]);

  useEffect(() => {
    loadPlaybook();
  }, [loadPlaybook]);

  const selectedStatus = selectedNodeId
    ? playbook?.statuses?.find((s) => s.id === selectedNodeId) ?? null
    : null;

  const selectedTransition = selectedEdgeId
    ? playbook?.transitions?.find((t) => t.id === selectedEdgeId) ?? null
    : null;

  // ── Add Status ──
  const addStatus = useCallback(async (position?: { x: number; y: number }) => {
    if (!playbookRef.current) return;
    const name = `New Status ${(playbookRef.current.statuses?.length ?? 0) + 1}`;
    try {
      const resp = await playbookApi.createStatus(playbookRef.current.id, {
        name,
        slug: generateSlug(name),
        category: 'todo',
        position_x: position?.x ?? 200,
        position_y: position?.y ?? 200,
      });
      const status = (resp as any)?.status ?? (resp as any)?.data ?? resp as any;
      const newNode = statusToNode(status);
      if (position) newNode.position = position;
      setNodes((nds) => [...nds, newNode]);
      setPlaybook((pb) => pb ? { ...pb, statuses: [...(pb.statuses ?? []), status] } : pb);
      setDirty(true);
      setSelectedNodeId(status.id);
      setSelectedEdgeId(null);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create status');
    }
  }, [setNodes]);

  // ── Update Status ──
  const updateStatus = useCallback(async (statusId: string, data: Partial<PlaybookStatusFull>) => {
    if (!playbookRef.current) return;
    try {
      const resp = await playbookApi.updateStatus(playbookRef.current.id, statusId, data);
      const updated = (resp as any)?.status ?? (resp as any)?.data ?? resp as any;
      setPlaybook((pb) => {
        if (!pb) return pb;
        return { ...pb, statuses: pb.statuses?.map((s) => s.id === statusId ? { ...s, ...updated } : s) };
      });
      setNodes((nds) => nds.map((n) => {
        if (n.id !== statusId) return n;
        return {
          ...n,
          data: {
            ...n.data,
            label: data.name ?? n.data.label,
            slug: data.slug ?? n.data.slug,
            category: data.category ?? n.data.category,
            description: data.description ?? n.data.description,
            is_initial: data.is_initial ?? n.data.is_initial,
            is_terminal: data.is_terminal ?? n.data.is_terminal,
            sla_hours: data.sla_hours !== undefined ? data.sla_hours : n.data.sla_hours,
            escalation_role: data.escalation_role !== undefined ? data.escalation_role : n.data.escalation_role,
            statusId: n.data.statusId,
          },
        };
      }));
      setDirty(true);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update status');
    }
  }, [setNodes]);

  // ── Delete Status ──
  const deleteStatus = useCallback(async (statusId: string) => {
    if (!playbookRef.current) return;
    try {
      await playbookApi.deleteStatus(playbookRef.current.id, statusId);
      setNodes((nds) => nds.filter((n) => n.id !== statusId));
      setEdges((eds) => eds.filter((e) => e.source !== statusId && e.target !== statusId));
      setPlaybook((pb) => {
        if (!pb) return pb;
        return {
          ...pb,
          statuses: pb.statuses?.filter((s) => s.id !== statusId),
          transitions: pb.transitions?.filter((t) => t.from_status_id !== statusId && t.to_status_id !== statusId),
        };
      });
      if (selectedNodeId === statusId) setSelectedNodeId(null);
      setDirty(true);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete status');
    }
  }, [selectedNodeId, setNodes, setEdges]);

  // ── Connect (create transition) ──
  const onConnect = useCallback(async (connection: Connection) => {
    if (!playbookRef.current || !connection.source || !connection.target) return;
    const fromStatus = playbookRef.current.statuses?.find((s) => s.id === connection.source);
    const toStatus = playbookRef.current.statuses?.find((s) => s.id === connection.target);
    const name = `${fromStatus?.name ?? 'From'} → ${toStatus?.name ?? 'To'}`;
    try {
      const resp = await playbookApi.createTransition(playbookRef.current.id, {
        name,
        slug: generateSlug(name),
        from_status_id: connection.source,
        to_status_id: connection.target,
        trigger_mode: 'manual',
      });
      const transition = (resp as any)?.transition ?? (resp as any)?.data ?? resp as any;
      const edge = transitionToEdge(transition);
      if (edge) {
        setEdges((eds) => [...eds, edge]);
        setPlaybook((pb) => pb ? { ...pb, transitions: [...(pb.transitions ?? []), transition] } : pb);
        setDirty(true);
        setSelectedEdgeId(transition.id);
        setSelectedNodeId(null);
      }
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create transition');
    }
  }, [setEdges]);

  // ── Update Transition ──
  const updateTransition = useCallback(async (transitionId: string, data: Partial<PlaybookTransitionFull>) => {
    if (!playbookRef.current) return;
    try {
      await playbookApi.updateTransition(playbookRef.current.id, transitionId, data as any);
      setPlaybook((pb) => {
        if (!pb) return pb;
        return { ...pb, transitions: pb.transitions?.map((t) => t.id === transitionId ? { ...t, ...data } : t) };
      });
      setEdges((eds) => eds.map((e) => {
        if (e.id !== transitionId) return e;
        return {
          ...e,
          data: {
            ...e.data!,
            label: (data.name as string) ?? e.data!.label,
            slug: (data.slug as string) ?? e.data!.slug,
            trigger_mode: (data.trigger_mode as string) ?? e.data!.trigger_mode,
            is_collaborative: data.is_collaborative ?? e.data!.is_collaborative,
          },
        };
      }));
      setDirty(true);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update transition');
    }
  }, [setEdges]);

  // ── Delete Transition ──
  const deleteTransition = useCallback(async (transitionId: string) => {
    if (!playbookRef.current) return;
    try {
      await playbookApi.deleteTransition(playbookRef.current.id, transitionId);
      setEdges((eds) => eds.filter((e) => e.id !== transitionId));
      setPlaybook((pb) => {
        if (!pb) return pb;
        return { ...pb, transitions: pb.transitions?.filter((t) => t.id !== transitionId) };
      });
      if (selectedEdgeId === transitionId) setSelectedEdgeId(null);
      setDirty(true);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete transition');
    }
  }, [selectedEdgeId, setEdges]);

  // ── Save Layout ──
  const saveLayout = useCallback(async () => {
    if (!playbookRef.current) return;
    setSaving(true);
    try {
      const layout: Record<string, { x: number; y: number }> = {};
      nodes.forEach((n) => { layout[n.id] = { x: n.position.x, y: n.position.y }; });
      await playbookApi.saveDiagramLayout(playbookRef.current.id, { diagram_layout: layout });
      setDirty(false);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save layout');
    } finally {
      setSaving(false);
    }
  }, [nodes]);

  // ── Refresh Playbook Data ──
  const refreshPlaybook = useCallback(async () => {
    if (!playbookRef.current) return;
    try {
      const resp = await playbookApi.getPlaybook(playbookRef.current.id);
      const pb = (resp as any)?.playbook ?? (resp as any)?.data?.playbook ?? resp as any;
      setPlaybook(pb);
    } catch {
      // silent
    }
  }, []);

  return {
    playbook,
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    selectedNodeId,
    selectedEdgeId,
    selectedStatus,
    selectedTransition,
    setSelectedNodeId,
    setSelectedEdgeId,
    loading,
    saving,
    error,
    dirty,
    drawingEdge,
    setDrawingEdge,
    setNodes,
    setEdges,
    addStatus,
    updateStatus,
    deleteStatus,
    onConnect,
    updateTransition,
    deleteTransition,
    saveLayout,
    refreshPlaybook,
    setError,
  };
}
